'use client';

import React, { useCallback, useEffect, useState } from 'react';
import { CircularProgress, PulseButton, Tooltip } from 'src/components/PulseUI';
import {
  faCheck,
  faRotate,
  faTriangleExclamation,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { useAuth } from 'src/context/auth';
import { useLogging } from 'src/context/logging';
import { useNotification } from 'src/context/notification';
import {
  useGetPlaysRegenerationJobQuery,
  useGetPlaysRegenerationJobsQuery,
  useGetProfileConfigsRegenerationJobQuery,
  useGetProfileConfigsRegenerationJobsQuery,
  useRegeneratePlaysMutation,
  useRegenerateProfileConfigsMutation,
} from 'src/api/redux';
import useAuthorization from 'src/hooks/authorization/useAuthorization';
import {
  FINGERPRINT_SYNTHESIS_BASE_URL,
  PLAYBOOK_FILE_NAME,
  PROFILE_CONFIGS_FILE_NAME,
} from './constants';
import { GitHubFileLink } from './components';

const POLL_INTERVAL_MS = 5_000;
const MAX_POLL_ERRORS = 3;

// The regenerate endpoints report failures as `error_message`; `detail` and
// `message` cover DRF defaults and network-level errors.
const getErrorMessage = (err: any, fallback: string): string =>
  err?.data?.error_message || err?.data?.detail || err?.message || fallback;

interface RegenerationJob {
  id: number;
  status: 'pending' | 'running' | 'completed' | 'failed';
  error_message: string | null;
  updated_at: string;
}

interface JobPollingState {
  jobId: number | null;
  isPolling: boolean;
  pollErrorCount: number;
}

const ProfileManager = () => {
  const log = useLogging();
  const { setNotification } = useNotification();
  const { isSupportUser } = useAuthorization();
  const { user } = useAuth();

  const companyFolder = user?.company?.name?.toLowerCase();

  const [regeneratePlays, { isLoading: isRegeneratingPlays }] =
    useRegeneratePlaysMutation();
  const [regenerateProfileConfigs, { isLoading: isRegeneratingConfigs }] =
    useRegenerateProfileConfigsMutation();

  // --- Plays job polling ---
  const [playsState, setPlaysState] = useState<JobPollingState>({
    jobId: null,
    isPolling: false,
    pollErrorCount: 0,
  });

  const { data: playsJobsListData } = useGetPlaysRegenerationJobsQuery(
    undefined,
    { skip: !isSupportUser }
  );

  useEffect(() => {
    if (playsState.jobId) return;
    const latest = playsJobsListData?.results?.[0];
    if (!latest) return;
    setPlaysState({
      jobId: latest.id,
      isPolling: latest.status === 'pending' || latest.status === 'running',
      pollErrorCount: 0,
    });
  }, [playsJobsListData, playsState.jobId]);

  const { data: playsJobData, error: playsPollError } =
    useGetPlaysRegenerationJobQuery(playsState.jobId!, {
      skip: !playsState.jobId || !playsState.isPolling,
      pollingInterval: POLL_INTERVAL_MS,
    });

  const activePlaysJob: RegenerationJob | undefined =
    playsJobData ??
    playsJobsListData?.results?.find((j) => j.id === playsState.jobId);

  useEffect(() => {
    if (!playsPollError) return;
    setPlaysState((s) => {
      const newCount = s.pollErrorCount + 1;
      if (newCount >= MAX_POLL_ERRORS) {
        setNotification({
          title: 'Error',
          message: 'Failed to check plays regeneration status.',
          type: 'error',
        });
        return { ...s, isPolling: false, pollErrorCount: newCount };
      }
      return { ...s, pollErrorCount: newCount };
    });
    log.exception(playsPollError, {
      tags: { context: 'profile-manager' },
    });
  }, [playsPollError, log, setNotification]);

  const handlePlaysTerminal = useCallback(
    (status: string, errorMessage: string | null) => {
      if (status === 'completed') {
        setPlaysState((s) => ({ ...s, isPolling: false }));
        setNotification({
          title: 'Success',
          message: 'Plays have been regenerated successfully.',
          type: 'success',
        });
      } else if (status === 'failed') {
        setPlaysState((s) => ({ ...s, isPolling: false }));
        setNotification({
          title: 'Error',
          message: errorMessage || 'Plays regeneration failed.',
          type: 'error',
        });
      }
    },
    [setNotification]
  );

  useEffect(() => {
    if (!playsJobData || !playsState.isPolling) return;
    handlePlaysTerminal(playsJobData.status, playsJobData.error_message);
  }, [playsJobData, playsState.isPolling, handlePlaysTerminal]);

  // --- Profile configs job polling ---
  const [configsState, setConfigsState] = useState<JobPollingState>({
    jobId: null,
    isPolling: false,
    pollErrorCount: 0,
  });

  const { data: configsJobsListData } =
    useGetProfileConfigsRegenerationJobsQuery(undefined, {
      skip: !isSupportUser,
    });

  useEffect(() => {
    if (configsState.jobId) return;
    const latest = configsJobsListData?.results?.[0];
    if (!latest) return;
    setConfigsState({
      jobId: latest.id,
      isPolling: latest.status === 'pending' || latest.status === 'running',
      pollErrorCount: 0,
    });
  }, [configsJobsListData, configsState.jobId]);

  const { data: configsJobData, error: configsPollError } =
    useGetProfileConfigsRegenerationJobQuery(configsState.jobId!, {
      skip: !configsState.jobId || !configsState.isPolling,
      pollingInterval: POLL_INTERVAL_MS,
    });

  const activeConfigsJob: RegenerationJob | undefined =
    configsJobData ??
    configsJobsListData?.results?.find((j) => j.id === configsState.jobId);

  useEffect(() => {
    if (!configsPollError) return;
    setConfigsState((s) => {
      const newCount = s.pollErrorCount + 1;
      if (newCount >= MAX_POLL_ERRORS) {
        setNotification({
          title: 'Error',
          message: 'Failed to check profile configs regeneration status.',
          type: 'error',
        });
        return { ...s, isPolling: false, pollErrorCount: newCount };
      }
      return { ...s, pollErrorCount: newCount };
    });
    log.exception(configsPollError, {
      tags: { context: 'profile-manager' },
    });
  }, [configsPollError, log, setNotification]);

  const handleConfigsTerminal = useCallback(
    (status: string, errorMessage: string | null) => {
      if (status === 'completed') {
        setConfigsState((s) => ({ ...s, isPolling: false }));
        setNotification({
          title: 'Success',
          message: 'Profile configs have been regenerated successfully.',
          type: 'success',
        });
      } else if (status === 'failed') {
        setConfigsState((s) => ({ ...s, isPolling: false }));
        setNotification({
          title: 'Error',
          message: errorMessage || 'Profile configs regeneration failed.',
          type: 'error',
        });
      }
    },
    [setNotification]
  );

  useEffect(() => {
    if (!configsJobData || !configsState.isPolling) return;
    handleConfigsTerminal(configsJobData.status, configsJobData.error_message);
  }, [configsJobData, configsState.isPolling, handleConfigsTerminal]);

  // --- Derived state ---
  const playsStatus = activePlaysJob?.status;
  const playsIsActive =
    playsState.isPolling ||
    playsStatus === 'pending' ||
    playsStatus === 'running';
  const playsDisabled = !isSupportUser || isRegeneratingPlays || playsIsActive;

  const configsStatus = activeConfigsJob?.status;
  const configsIsActive =
    configsState.isPolling ||
    configsStatus === 'pending' ||
    configsStatus === 'running';
  const configsDisabled =
    !isSupportUser || isRegeneratingConfigs || configsIsActive;

  // --- Handlers ---
  const handleRegeneratePlays = async () => {
    try {
      const job = await regeneratePlays().unwrap();
      setPlaysState({ jobId: job.id, isPolling: true, pollErrorCount: 0 });
    } catch (err: any) {
      log.exception(err, {
        tags: { context: 'profile-manager' },
      });
      setNotification({
        title: 'Error',
        message: getErrorMessage(err, 'Error regenerating plays'),
        type: 'error',
      });
    }
  };

  const handleRegenerateProfileConfigs = async () => {
    try {
      const job = await regenerateProfileConfigs().unwrap();
      setConfigsState({ jobId: job.id, isPolling: true, pollErrorCount: 0 });
    } catch (err: any) {
      log.exception(err, {
        tags: { context: 'profile-manager' },
      });
      setNotification({
        title: 'Error',
        message: getErrorMessage(err, 'Error regenerating profile configs'),
        type: 'error',
      });
    }
  };

  // --- Status rendering ---
  const formatTimestamp = (iso: string | undefined): string => {
    if (!iso) return '';
    const date = new Date(iso);
    return date.toLocaleString();
  };

  const renderJobStatus = (
    jobId: number | null,
    isActive: boolean,
    status: string | undefined,
    errorMessage: string | null | undefined,
    updatedAt: string | undefined,
    label: string,
    testIdPrefix: string
  ) => {
    if (!jobId) return null;

    if (isActive) {
      return (
        <div
          className="flex items-center gap-2 text-sm text-sky-600"
          role="status"
          aria-live="polite"
          data-testid={`${testIdPrefix}-status-active`}
        >
          <CircularProgress size="sm" aria-label={`Regenerating ${label}`} />
          <span>Regenerating {label}…</span>
        </div>
      );
    }

    if (status === 'completed') {
      return (
        <div
          className="flex items-center gap-2 text-sm text-green-600"
          role="status"
          aria-live="polite"
          data-testid={`${testIdPrefix}-status-completed`}
        >
          <Icon icon={faCheck} className="h-4 w-4" />
          <span>
            Last regenerated successfully
            {updatedAt ? ` on ${formatTimestamp(updatedAt)}` : ''}
          </span>
        </div>
      );
    }

    if (status === 'failed') {
      return (
        <div
          className="flex items-center gap-2 text-sm text-red-600"
          role="alert"
          aria-live="assertive"
          data-testid={`${testIdPrefix}-status-failed`}
        >
          <Icon icon={faTriangleExclamation} className="h-4 w-4" />
          <span>{errorMessage || `${label} regeneration failed`}</span>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="flex flex-col gap-6 p-6" data-testid="profile-manager">
      <div className="flex flex-col gap-2">
        <h2 className="text-xl font-semibold text-neutral-700">
          Profile Management
        </h2>
        <p className="text-sm text-neutral-500">
          Regenerate AI-generated profile content for the current company.
          Regeneration runs in the background and may take several minutes to
          complete.
        </p>
      </div>
      <div className="flex flex-col gap-2">
        <h3 className="text-lg font-semibold text-neutral-700">Plays</h3>
        <p className="text-sm text-neutral-500">
          This will regenerate the plays for this company from their playbook in
          GitHub, replacing the company&apos;s current plays.
        </p>
        <div className="flex items-center gap-2">
          <Tooltip
            content={
              !isSupportUser
                ? 'Only support users can regenerate plays'
                : undefined
            }
          >
            <PulseButton
              loading={isRegeneratingPlays}
              disabled={playsDisabled}
              onClick={handleRegeneratePlays}
              startDecorator={faRotate}
              color="primary"
              aria-label="Regenerate plays"
              data-testid="regenerate-plays-button"
            >
              Regenerate Plays
            </PulseButton>
          </Tooltip>
          {companyFolder && (
            <GitHubFileLink
              href={`${FINGERPRINT_SYNTHESIS_BASE_URL}/${encodeURIComponent(companyFolder)}/${PLAYBOOK_FILE_NAME}`}
              aria-label="View playbook in GitHub (opens in new tab)"
              data-testid="playbook-github-button"
            >
              View Playbook in GitHub
            </GitHubFileLink>
          )}
        </div>
        {renderJobStatus(
          playsState.jobId,
          playsIsActive,
          playsStatus,
          activePlaysJob?.error_message,
          activePlaysJob?.updated_at,
          'plays',
          'plays'
        )}
      </div>
      <div className="flex flex-col gap-2">
        <h3 className="text-lg font-semibold text-neutral-700">
          Profile Configs
        </h3>
        <p className="text-sm text-neutral-500">
          This will regenerate the profile page layouts for this company from
          their profile config research in GitHub, replacing the company&apos;s
          current profile configs.
        </p>
        <div className="flex items-center gap-2">
          <Tooltip
            content={
              !isSupportUser
                ? 'Only support users can regenerate profile configs'
                : undefined
            }
          >
            <PulseButton
              loading={isRegeneratingConfigs}
              disabled={configsDisabled}
              onClick={handleRegenerateProfileConfigs}
              startDecorator={faRotate}
              color="primary"
              aria-label="Regenerate profile configs"
              data-testid="regenerate-profile-configs-button"
            >
              Regenerate Profile Configs
            </PulseButton>
          </Tooltip>
          {companyFolder && (
            <GitHubFileLink
              href={`${FINGERPRINT_SYNTHESIS_BASE_URL}/${encodeURIComponent(companyFolder)}/${PROFILE_CONFIGS_FILE_NAME}`}
              aria-label="View profile config in GitHub (opens in new tab)"
              data-testid="profile-configs-github-button"
            >
              View Profile Config in GitHub
            </GitHubFileLink>
          )}
        </div>
        {renderJobStatus(
          configsState.jobId,
          configsIsActive,
          configsStatus,
          activeConfigsJob?.error_message,
          activeConfigsJob?.updated_at,
          'profile configs',
          'configs'
        )}
      </div>
    </div>
  );
};

export default ProfileManager;
